import React from 'react';
import { Text, StatusTimeline } from '@chatsdet/ui';
import type { QaReportTodoStatus } from '../page-detail/types';

export type ElementTest = {
  id: string;
  name: string;
  status: QaReportTodoStatus;
  completedAt?: string | null;
};

const STATUS_TONE: Record<QaReportTodoStatus, 'success' | 'danger' | 'warning' | 'muted'> = {
  healthy: 'success',
  done: 'success',
  unhealthy: 'danger',
  pending: 'muted',
  not_applicable: 'warning',
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown time';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export function ElementTestTimeline({ tests }: { tests: ElementTest[] }) {
  if (!tests || tests.length === 0) {
    return (
      <Text size="sm" className="text-muted-foreground">
        No tests yet
      </Text>
    );
  }

  const entries = tests.slice(0, 10).map((test) => ({
    id: test.id,
    tone: STATUS_TONE[test.status] ?? 'muted',
    label: test.name,
    tooltip: `${test.name} • ${formatTimestamp(test.completedAt)} • ${statusLabel(test.status)}`,
  }));

  return (
    <StatusTimeline
      entries={entries}
      slotCount={Math.max(4, entries.length)}
      size="sm"
      showNavigation={false}
    />
  );
}

const statusLabel = (status: QaReportTodoStatus): string => {
  switch (status) {
    case 'healthy':
    case 'done':
      return 'Passed';
    case 'unhealthy':
      return 'Failed';
    case 'not_applicable':
      return 'Not applicable';
    case 'pending':
    default:
      return 'Pending';
  }
};
